import type { PaperPriceModel } from "./paper-trading-config";

/** Place/preview rejection codes added by the price models; the rest live in the API's `PaperTradingErrorCode`. */
export const PAPER_ORDER_REJECTION_CODES = {
  /** TOUCH/BOOK: no fresh touch or book inside the staleness threshold; never filled at a fallback mid. */
  STALE_FEED: "PAPER_STALE_FEED",
  /** BOOK model: the side has no ladder to extend past the visible book (fewer than 2 levels). */
  BEYOND_BOOK_DEPTH: "PAPER_BEYOND_BOOK_DEPTH",
} as const;
export type PaperOrderRejectionCode = (typeof PAPER_ORDER_REJECTION_CODES)[keyof typeof PAPER_ORDER_REJECTION_CODES];

/** One projected fill. `SYNTHETIC` when part of it lands past the visible book (BOOK). Decimal fields are strings. */
export type PaperOrderPreviewFillDto = {
  priceUSD: string;
  sizeBase: string;
  notionalUSD: string;
  feeUSD: string;
  source: "HL" | "SYNTHETIC";
};

/** Response of `POST /v1/hub/paper-trading/orders/preview`. Decimal fields are strings. */
export type PaperOrderPreviewDto = {
  /** Size the model fills now: the requested size, `maxSize` when it is beyond the book, 0 for a limit that rests. */
  totalFilledSize: string;
  /** Estimated size-weighted fill price. */
  averageFillPriceUSD: string;
  totalNotionalUSD: string;
  totalFeeUSD: string;
  fills: PaperOrderPreviewFillDto[];
  /** BOOK: the requested size exceeds `maxSize`; placing it is rejected with `PAPER_BEYOND_BOOK_DEPTH`. */
  exhaustedDepth: boolean;
  /** `collateralUSD − totalFeeUSD`; realized PnL on flips is not modelled. */
  postTradeCollateralUSD: string;
  /** Post-trade collateral would not cover this position's maintenance margin. */
  marginBreachRisk: boolean;
  priceModel: PaperPriceModel;
  /** Signed cost vs filling the same size at mid: (avg − mid) × size for a BUY, (mid − avg) × size for a SELL. */
  spreadSlippageUSD: string;
  /** Maker for a resting limit, taker otherwise. */
  feeBps: number;
  /** Largest size the model accepts on this side; null when uncapped (MID, TOUCH). */
  maxSize: string | null;
};
